import {
  BO_NOTIFICATION_SUBSCRIBER_DELIMITER,
  DEFAULT_CATEGORY_NAME,
  SERVICE_TAG_VARIANT_DELIMITER,
} from '../models/consts';
import {
  __TYPE__,
  APP_MODE,
  BUSINESS_OBJECT_VERSION,
  DATE_FORMAT,
  WEEKDAY,
} from '../models/enums';
import {
  BusinessObject,
  DateRange,
  Product,
  ProductCategory,
  ProductHappyHour,
  ServiceTag,
  Staff,
  TwConnect,
  TwEmployee,
  TwMenu,
  TwOffer,
  TwOfferGroup,
  TwProConnect,
  TwSku,
} from '../models/interfaces';
import { DateTimeUtils, NumberUtils, ReviewUtils, StringUtils } from './';

export class BusinessObjectUtils {
  // region Business object
  public static isProVersion(bo: BusinessObject): boolean {
    return bo?.version === BUSINESS_OBJECT_VERSION.PRO;
  }

  public static shouldDisplayFullContactDataOfCustomer(
    bo: BusinessObject,
    appMode: APP_MODE
  ): boolean {
    if (appMode === APP_MODE.CHECKIN || appMode === APP_MODE.BOOKING) {
      return false;
    }

    if (appMode === APP_MODE.STAFF) {
      return bo?.showCustomerDataStaffApp;
    }

    if (appMode === APP_MODE.CHECKOUT) {
      return bo?.showCustomerDataCheckout;
    }

    return true;
  }

  public static validateDateRangeBasingOnAppointmentLifeTime(
    bo: BusinessObject,
    appMode: APP_MODE,
    originalDateRange: DateRange
  ): DateRange {
    if (appMode === APP_MODE.ADMIN) {
      return originalDateRange;
    }

    const isAppointmentLifeTimeRestricted =
      NumberUtils.isNumber(bo?.appointmentLifeTimeInDays) &&
      bo?.appointmentLifeTimeInDays >= 0;

    if (!isAppointmentLifeTimeRestricted) {
      return originalDateRange;
    }

    const latestFromDate = DateTimeUtils.addDayToDate(
      new Date(),
      -bo?.appointmentLifeTimeInDays
    );

    return this.validateDateRangeBasingOnLatestFromDate(
      originalDateRange,
      latestFromDate
    );
  }

  public static validateDateRangeBasingOnOrderLifeTime(
    bo: BusinessObject,
    appMode: APP_MODE,
    originalDateRange: DateRange
  ): DateRange {
    if (appMode === APP_MODE.ADMIN) {
      return originalDateRange;
    }

    const isOrderLifeTimeRestricted =
      NumberUtils.isNumber(bo?.orderLifetimeInDays) &&
      bo?.orderLifetimeInDays >= 0;

    if (!isOrderLifeTimeRestricted) {
      return originalDateRange;
    }

    const latestFromDate = DateTimeUtils.addDayToDate(
      new Date(),
      -bo?.orderLifetimeInDays + 1
    );

    return this.validateDateRangeBasingOnLatestFromDate(
      originalDateRange,
      latestFromDate
    );
  }

  private static validateDateRangeBasingOnLatestFromDate(
    originalDateRange: DateRange,
    latestFromDate: Date
  ): DateRange {
    const latestFromDateStr = DateTimeUtils.formatDateToString(
      latestFromDate,
      DATE_FORMAT.DATE_FORMAT_BACKEND
    );
    const fromDateStr = DateTimeUtils.formatDateToString(
      originalDateRange.fromDate,
      DATE_FORMAT.DATE_FORMAT_BACKEND
    );
    const toDateStr = DateTimeUtils.formatDateToString(
      originalDateRange.toDate,
      DATE_FORMAT.DATE_FORMAT_BACKEND
    );

    if (latestFromDateStr > toDateStr) {
      return null;
    }

    return {
      fromDate:
        latestFromDateStr > fromDateStr
          ? latestFromDate
          : originalDateRange.fromDate,
      toDate: originalDateRange.toDate,
    };
  }

  public static getSelectedProducts(bo: BusinessObject): Product[] {
    return this.getUniqueProductListOfBo(bo)?.filter((p) => p.isSelected);
  }

  public static getUniqueProductListOfBo(bo: BusinessObject): Product[] {
    const productList: Product[] = [];

    bo?.categories?.forEach((category) => {
      category?.products?.forEach((product) => productList.push(product));
    });

    return productList;
  }

  public static getListOfProducts(
    bo: BusinessObject,
    productIds: number[]
  ): Product[] {
    const result = [];

    if (productIds?.length) {
      bo?.categories?.forEach((category) => {
        category?.products?.forEach((product) => {
          if (productIds?.includes(product.id)) {
            result.push(product);
          }
        });
      });
    }

    return result;
  }

  public static setSelectedProductForBo(
    bo: BusinessObject,
    products: Product[]
  ): BusinessObject {
    const productIds = new Set<number>(products?.map((p) => p.id));

    bo.categories?.forEach((category) => {
      category?.products?.forEach(
        (product) => (product.isSelected = productIds.has(product.id))
      );
    });

    return bo;
  }

  public static isAllProductsSelected(bo: BusinessObject): boolean {
    const numberOfProducts = this.getUniqueProductListOfBo(bo)?.length || 0;
    const numberOfSelectedProducts = this.getSelectedProducts(bo)?.length || 0;

    return numberOfSelectedProducts === numberOfProducts;
  }

  public static getMapOfIdAndProduct(bo: BusinessObject): Map<number, Product> {
    const map = new Map<number, Product>();

    bo?.categories?.forEach((c) => {
      c?.products?.forEach((p) => {
        map.set(p.id, p);
      });
    });

    return map;
  }

  public static getAllProductsFromBo(bo: BusinessObject): Product[] {
    const products: Product[] = [];

    bo?.categories?.find((category) => {
      category?.products?.forEach((p) => {
        if (p?.id) {
          products.push(p);
        }
      });
    });

    return products;
  }

  public static getProductFromBo(
    bo: BusinessObject,
    productId: number
  ): Product {
    let product: Product;

    bo?.categories?.find((category) => {
      category?.products?.forEach((p) => {
        if (p.id === productId) {
          product = p;
        }
      });
    });

    return product;
  }

  public static findBestMatchedCategoryWithName(
    bo: BusinessObject,
    categoryName: string
  ): ProductCategory {
    if (!bo?.categories?.length) {
      return null;
    }

    let bestMatchedPercentage = 0;
    let bestMatchedCategory: ProductCategory;

    for (const category of bo.categories) {
      const percentage = StringUtils.calculateIdenticalPercentageOfTwoString(
        category.name,
        categoryName
      );

      if (
        percentage >= StringUtils.STRING_IDENTICAL_THRESHOLD_PERCENTAGE &&
        percentage > bestMatchedPercentage
      ) {
        bestMatchedPercentage = percentage;
        bestMatchedCategory = category;
      }
    }

    return bestMatchedCategory;
  }

  public static calculateTotalDuration(
    bo: BusinessObject,
    productIds: number[]
  ): number {
    let duration = 0;
    const listOfProducts = this.getListOfProducts(bo, productIds);
    listOfProducts?.forEach((p) => (duration += p.duration));
    return duration;
  }

  public static transformServiceTagForFrontend(
    serviceTag: ServiceTag
  ): ServiceTag {
    if (!serviceTag) {
      return serviceTag;
    }

    return {
      ...serviceTag,
      variantList: serviceTag.variant?.split(SERVICE_TAG_VARIANT_DELIMITER),
    };
  }

  public static transformBoForBackend(
    businessObject: BusinessObject
  ): BusinessObject {
    return {
      ...businessObject,
      __type__: businessObject.__type__ || __TYPE__.BASIC_BUSINESS_OBJECT,
      currency: businessObject.currency || 'CHF',
      spendAmountToBonusPointConversionRate:
        businessObject.spendAmountToBonusPointConversionRate || 1,
    };
  }

  public static transformCategoryForBackend(
    category: ProductCategory
  ): ProductCategory {
    return {
      ...category,
      __type__: category.__type__ || __TYPE__.BASIC_PRODUCT_CATEGORY,
    };
  }

  public static transformProductForBackend(product: Product): Product {
    return {
      ...product,
      __type__: product.__type__ || __TYPE__.BASIC_PRODUCT,
    };
  }

  public static transformBoForFrontend(
    bo: BusinessObject,
    isPublic: boolean
  ): BusinessObject {
    if (!bo) {
      return bo;
    }

    if (bo.categories?.length > 0) {
      bo.categories = bo.categories
        .filter((cat) => cat.name !== DEFAULT_CATEGORY_NAME)
        .map((category) =>
          this.transformCategoryForFrontend(category, isPublic)
        );

      bo.categories.sort((c1, c2) => {
        if (!c1.displayOrder && !c2.displayOrder) {
          if (c1.id < c2.id) {
            return -1;
          }
          if (c1.id > c2.id) {
            return 1;
          }
          return 0;
        }

        if (c1.displayOrder < c2.displayOrder) {
          return -1;
        }
        if (c1.displayOrder > c2.displayOrder) {
          return 1;
        }
        return 0;
      });
    }

    bo.twConnect = this.transformTwConnectForFrontend(bo.twConnect);
    bo.twPro = this.transformTwProConnectForFrontend(bo.twPro);

    if (bo?.teaserImages?.length > 1) {
      bo.teaserImages.sort((t1, t2) => {
        if (t1.id < t2.id) {
          return -1;
        }
        if (t1.id > t2.id) {
          return 1;
        }
        return 0;
      });
    }

    if (bo.boNotificationSettings) {
      bo.boNotificationSettings.subscriberList = bo.boNotificationSettings
        .subscribers?.length
        ? bo.boNotificationSettings.subscribers?.split(
            BO_NOTIFICATION_SUBSCRIBER_DELIMITER
          )
        : [];
    }

    // TODO: remove it
    if (bo?.urlName === 'new-york-nails-schlieren') {
      bo.googleMapsEmbedLink =
        'https://www.google.com/maps/embed?pb=!4v1665590044160!6m8!1m7!1sCAoSLEFGMVFpcE5XVnNyblgzUk9jSzdlaExram5rOW5RX2VHalI5eUVqdmRHWHVU!2m2!1d47.398234707928!2d8.4475526600842!3f51.20391691360178!4f-11.847227523464525!5f0.7820865974627469';
    }

    return bo;
  }

  public static sortMatchedSalonsByAdjustedRating(
    salons: BusinessObject[]
  ): BusinessObject[] {
    const globaleAverageRating = this.calculateGlobalAverage(salons);

    if (!globaleAverageRating) {
      return [...salons];
    }

    return [...salons].sort(
      (a, b) =>
        this.getAdjustedRating(b, globaleAverageRating) -
        this.getAdjustedRating(a, globaleAverageRating)
    );
  }

  private static calculateGlobalAverage(
    matchedSalons: BusinessObject[]
  ): number {
    let totalRatingStars = 0;
    let nr = 0;

    matchedSalons?.forEach((salon) => {
      if (salon?.googlePlace?.user_ratings_total) {
        totalRatingStars += salon.googlePlace.rating || 0;
        nr++;
      }
    });

    if (!nr) {
      return 0;
    }

    return totalRatingStars / nr;
  }

  private static getAdjustedRating(
    salon: BusinessObject,
    globaleAverageRating: number
  ): number {
    const result = ReviewUtils.calculateAdjustedRating(
      salon?.googlePlace?.rating,
      salon?.googlePlace?.user_ratings_total,
      globaleAverageRating
    );

    return result;
  }

  // endregion

  // region Category
  public static transformCategoryForFrontend(
    category: ProductCategory,
    isPublic: boolean
  ): ProductCategory {
    if (!category?.products?.length) {
      return category;
    }

    if (isPublic) {
      category.products = category.products?.filter((p) => !!p.enabled4Booking);
    }

    category.products.sort((p1, p2) => {
      if (!p1.displayOrder && !p2.displayOrder) {
        if (p1.id < p2.id) {
          return -1;
        }
        if (p1.id > p2.id) {
          return 1;
        }
        return 0;
      }

      if (p1.displayOrder < p2.displayOrder) {
        return -1;
      }
      if (p1.displayOrder > p2.displayOrder) {
        return 1;
      }
      return 0;
    });

    return category;
  }

  public static isCategoryHasSpecialist(
    category: ProductCategory,
    staffId: number
  ): boolean {
    if (!category.products?.length) {
      return true;
    }

    let isSpecialistOfAllProducts = true;

    category.products.forEach((p) => {
      isSpecialistOfAllProducts =
        isSpecialistOfAllProducts && this.isProductHasSpecialist(p, staffId);
    });

    return isSpecialistOfAllProducts;
  }

  public static isContainAnyProductWithActivatedDiscount(
    category: ProductCategory
  ): boolean {
    return (
      category?.products?.findIndex((p) => this.isAnyDiscountActivated(p)) >= 0
    );
  }

  public static isContainAnyProductWithActivatedDiscountAtTimeStamp(
    category: ProductCategory,
    timeStamp: Date
  ): boolean {
    return (
      category?.products?.findIndex((p) =>
        this.isAnyDiscountActivatedAtTimeStamp(p, timeStamp)
      ) >= 0
    );
  }

  // endregion

  // region Product

  public static isProductHasSpecialist(
    product: Product,
    staffId: number
  ): boolean {
    if (!product.specialists?.length) {
      return true;
    }

    return product.specialists.indexOf(staffId) >= 0;
  }

  public static isContainStaffIdAsSpecialist(
    product: Product,
    staffId: number
  ): boolean {
    if (!product?.specialists?.length) {
      return true;
    }

    return product.specialists.indexOf(staffId) >= 0;
  }

  public static getListOfProductsWithAnyDiscountActivated(
    bo: BusinessObject,
    timeStamp?: Date
  ): Product[] {
    const result: Product[] = [];

    bo?.categories?.forEach((category) => {
      category?.products?.forEach((product) => {
        if (
          (timeStamp &&
            this.isAnyDiscountActivatedAtTimeStamp(product, timeStamp)) ||
          (!timeStamp && this.isAnyDiscountActivated(product))
        ) {
          result.push(product);
        }
      });
    });

    return result;
  }

  public static isAnyDiscountActivated(product: Product): boolean {
    return (
      this.isHappyHoursActivated(product) || this.isPromotionActivated(product)
    );
  }

  public static isHappyHoursActivated(product: Product): boolean {
    return (
      !!product &&
      !!product.happyHours?.activated &&
      !!product.happyHours?.fromHour &&
      !!product.happyHours?.toHour &&
      !!product.happyHours?.weekDays?.length &&
      !!product.happyHours?.amount &&
      product.happyHours.amount > 0
    );
  }

  public static isPromotionActivated(product: Product): boolean {
    if (
      !product ||
      !product.promotion?.activated ||
      !product.promotion?.toDate ||
      !product.promotion?.fromDate ||
      !product.promotion?.amount ||
      product.promotion.amount <= 0
    ) {
      return false;
    }

    const today = DateTimeUtils.getBeginOfDate(new Date());
    const endDate = DateTimeUtils.getEndOfDate(
      DateTimeUtils.parseStringToDate(product.promotion.toDate)
    );

    return endDate > today;
  }

  public static isAnyDiscountActivatedAtTimeStamp(
    product: Product,
    timeStamp: Date
  ): boolean {
    return (
      this.isHappyHoursActivatedAtTimeStamp(product, timeStamp) ||
      this.isPromotionActivatedAtTimeStamp(product, timeStamp)
    );
  }

  public static isHappyHoursActivatedAtTimeStamp(
    product: Product,
    timeStamp: Date
  ): boolean {
    if (!product || !this.isHappyHoursActivated(product) || !timeStamp) {
      return false;
    }

    if (
      !this.isHappyHourContainWeekday(product.happyHours, timeStamp.getDay())
    ) {
      return false;
    }

    const time: string = DateTimeUtils.formatTimeToString(timeStamp);

    return (
      product.happyHours.fromHour <= time && time <= product.happyHours.toHour
    );
  }

  public static isPromotionActivatedAtTimeStamp(
    product: Product,
    timeStamp: Date
  ): boolean {
    if (!product || !this.isPromotionActivated(product) || !timeStamp) {
      return false;
    }

    const promotionStartTime = DateTimeUtils.getBeginOfDate(
      DateTimeUtils.parseStringToDate(product.promotion.fromDate)
    );

    const promotionEndTime = DateTimeUtils.getEndOfDate(
      DateTimeUtils.parseStringToDate(product.promotion.toDate)
    );

    return promotionStartTime <= timeStamp && timeStamp <= promotionEndTime;
  }

  public static isPromotionExpiredAtTimeStamp(
    product: Product,
    timeStamp: Date
  ): boolean {
    if (!this.isPromotionActivated(product) || !timeStamp) {
      return false;
    }

    const promotionEndTime = DateTimeUtils.getEndOfDate(
      DateTimeUtils.parseStringToDate(product.promotion.toDate)
    );

    return timeStamp > promotionEndTime;
  }

  public static getAnyActivatePromotionalPrice(product: Product): number {
    if (this.isPromotionActivated(product)) {
      return this.calculatePromotionPrice(product);
    }

    if (this.isHappyHoursActivated(product)) {
      return this.calculateHappyHourPrice(product);
    }

    return product.price;
  }

  public static getPromotionalPrice(
    product: Product,
    selectedAppointmentDateTime: Date
  ): number {
    if (
      this.isPromotionActivatedAtTimeStamp(product, selectedAppointmentDateTime)
    ) {
      return this.calculatePromotionPrice(product);
    }

    if (
      this.isHappyHoursActivatedAtTimeStamp(
        product,
        selectedAppointmentDateTime
      )
    ) {
      return this.calculateHappyHourPrice(product);
    }

    return null;
  }

  private static isHappyHourContainWeekday(
    productHappyHour: ProductHappyHour,
    weekdayAsNumber: number
  ): boolean {
    if (!productHappyHour) {
      return false;
    }
    const weekdayAsStr = [
      WEEKDAY.MONDAY,
      WEEKDAY.TUESDAY,
      WEEKDAY.WEDNESDAY,
      WEEKDAY.THURSDAY,
      WEEKDAY.FRIDAY,
      WEEKDAY.SATURDAY,
      WEEKDAY.SUNDAY,
    ];
    const weekDaysAsNumber = productHappyHour.weekDays?.map(
      (weekday) => (weekdayAsStr.indexOf(weekday) + 1) % 7
    );

    return weekDaysAsNumber.indexOf(weekdayAsNumber) >= 0;
  }

  public static calculatePromotionPrice(product: Product): number {
    return this.calculatePromotionalPrice(
      product.price,
      product.promotion?.amount,
      product.promotion?.percentage
    );
  }

  public static calculateHappyHourPrice(product: Product): number {
    return this.calculatePromotionalPrice(
      product.price,
      product.happyHours?.amount,
      product.happyHours?.percentage
    );
  }

  public static calculatePromotionalPrice(
    price: number,
    amount: number,
    isPercentage: boolean
  ): number {
    if (!amount || amount <= 0) {
      return price;
    }

    const discountedPrice = isPercentage
      ? price * (1 - amount / 100)
      : price - amount;
    return Math.max(0, discountedPrice);
  }

  // endregion

  // region TW Pro

  public static isConnectionWithTreatwellProHealthy(
    bo: BusinessObject
  ): boolean {
    // currently there is no way to identify healthy of TW Pro connection
    return !!bo?.twPro?.id && !!bo?.twPro?.venueId && bo?.twPro?.syncActive;
  }

  private static transformTwConnectForFrontend(
    twConnect: TwConnect
  ): TwConnect {
    if (!twConnect) {
      return null;
    }

    if (twConnect?.atktExpired) {
      twConnect.atktExpiredAsDate = DateTimeUtils.parseStringToDate(
        twConnect.atktExpired
      );
    }

    if (twConnect?.itktExpired) {
      twConnect.itktExpiredAsDate = DateTimeUtils.parseStringToDate(
        twConnect.itktExpired
      );
    }

    if (twConnect?.lastModified) {
      twConnect.lastModifiedAsDate = DateTimeUtils.parseStringToDate(
        twConnect.lastModified
      );
    }

    if (twConnect?.createdTime) {
      twConnect.createdTimeAsDate = DateTimeUtils.parseStringToDate(
        twConnect.createdTime
      );
    }

    return twConnect;
  }

  private static transformTwProConnectForFrontend(
    twProConnect: TwProConnect
  ): TwProConnect {
    if (!twProConnect) {
      return null;
    }

    if (twProConnect?.cookieLastUpdate) {
      twProConnect.cookieLastUpdateAsDate = DateTimeUtils.parseStringToDate(
        twProConnect.cookieLastUpdate
      );
    }
    if (twProConnect?.calendarLastSync) {
      twProConnect.calendarLastSyncAsDate = DateTimeUtils.parseStringToDate(
        twProConnect.calendarLastSync
      );
    }
    if (twProConnect?.createdTime) {
      twProConnect.createdTimeAsDate = DateTimeUtils.parseStringToDate(
        twProConnect.createdTime
      );
    }
    if (twProConnect?.lastModified) {
      twProConnect.lastModifiedAsDate = DateTimeUtils.parseStringToDate(
        twProConnect.lastModified
      );
    }

    return twProConnect;
  }

  // endregion

  // region TW Connect

  public static isConnectedWithTreatwellConnectVenue(
    bo: BusinessObject
  ): boolean {
    return (
      bo?.twConnect?.venueId &&
      bo?.twConnect?.name &&
      this.isTreatwellConnectConnectionHealthy(bo)
    );
  }

  public static isTreatwellConnectConnectionHealthy(
    bo: BusinessObject
  ): boolean {
    return bo?.twConnect?.syncActive && !this.isTwConnectExpired(bo);
  }

  public static getTwConnectExpirationDate(bo: BusinessObject): Date {
    if (
      !bo?.twConnect?.itktExpiredAsDate ||
      !bo?.twConnect?.atktExpiredAsDate
    ) {
      return null;
    }

    return bo.twConnect.itktExpiredAsDate > bo.twConnect.atktExpiredAsDate
      ? bo.twConnect.atktExpiredAsDate
      : bo.twConnect.itktExpiredAsDate;
  }

  private static isTwConnectExpired(bo: BusinessObject): boolean {
    const now = new Date();

    return this.getTwConnectExpirationDate(bo) < now;
  }

  public static generateStaffToUpdateComparingWithTwEmployee(
    staff: Staff,
    bo: BusinessObject,
    twEmployee: TwEmployee
  ): Staff {
    const staffToUpdate = {
      ...(staff || {}),
    };

    if (this.isNameOfStaffChangedComparingWithTwEmployee(staff, twEmployee)) {
      const nameArr = twEmployee.name?.trim()?.split(' ');

      if (nameArr.length < 2) {
        staffToUpdate.name = twEmployee.name.trim();
        staffToUpdate.surname = ' ';
      } else {
        staffToUpdate.name = nameArr.slice(0, -1).join(' ');
        staffToUpdate.surname = nameArr[nameArr.length - 1];
      }
    }

    if (this.isEmailOfStaffChangedComparingWithTwEmployee(staff, twEmployee)) {
      staffToUpdate.email = StringUtils.simplifyString(twEmployee.emailAddress);
    }

    if (this.isPhoneOfStaffChangedComparingWithTwEmployee(staff, twEmployee)) {
      staffToUpdate.phoneNr = StringUtils.simplifyString(twEmployee.phone);
    }

    if (
      this.isActivationOfStaffChangedComparingWithTwEmployee(staff, twEmployee)
    ) {
      staffToUpdate.active = !!twEmployee.active;
    }

    if (
      this.isBookingActivationOfStaffChangedComparingWithTwEmployee(
        staff,
        twEmployee
      )
    ) {
      staffToUpdate.enabled4Booking = !!twEmployee.takesAppointments;
    }

    if (!!twEmployee?.id) {
      const matchedWorkspaceIndex = staffToUpdate?.workplaces?.findIndex(
        (w) => w.businessObjectId === bo?.id
      );
      if (matchedWorkspaceIndex >= 0) {
        staffToUpdate.workplaces[matchedWorkspaceIndex].twEmployeeId =
          twEmployee.id;
      }
    }

    return staffToUpdate;
  }

  public static isStaffChangedComparingWithTwEmployee(
    staff: Staff,
    twEmployee: TwEmployee
  ): boolean {
    return (
      this.isNameOfStaffChangedComparingWithTwEmployee(staff, twEmployee) ||
      this.isEmailOfStaffChangedComparingWithTwEmployee(staff, twEmployee) ||
      this.isPhoneOfStaffChangedComparingWithTwEmployee(staff, twEmployee) ||
      this.isActivationOfStaffChangedComparingWithTwEmployee(
        staff,
        twEmployee
      ) ||
      this.isBookingActivationOfStaffChangedComparingWithTwEmployee(
        staff,
        twEmployee
      )
    );
  }

  public static isNameOfStaffChangedComparingWithTwEmployee(
    staff: Staff,
    twEmployee: TwEmployee
  ): boolean {
    return (
      `${staff?.name || ''} ${staff?.surname || ''}`.trim() !==
      twEmployee.name.trim()
    );
  }

  public static isEmailOfStaffChangedComparingWithTwEmployee(
    staff: Staff,
    twEmployee: TwEmployee
  ): boolean {
    return (
      StringUtils.simplifyString(staff?.email) !==
      StringUtils.simplifyString(twEmployee?.emailAddress)
    );
  }

  public static isPhoneOfStaffChangedComparingWithTwEmployee(
    staff: Staff,
    twEmployee: TwEmployee
  ): boolean {
    return (
      StringUtils.simplifyString(staff?.phoneNr) !==
      StringUtils.simplifyString(twEmployee?.phone)
    );
  }

  public static isActivationOfStaffChangedComparingWithTwEmployee(
    staff: Staff,
    twEmployee: TwEmployee
  ): boolean {
    return !!staff?.active !== !!twEmployee?.active;
  }

  public static isBookingActivationOfStaffChangedComparingWithTwEmployee(
    staff: Staff,
    twEmployee: TwEmployee
  ): boolean {
    return !!staff?.enabled4Booking !== !!twEmployee?.takesAppointments;
  }

  public static findBestMatchedTwEmployeeWithName(
    twEmployees: TwEmployee[],
    name: string
  ): TwEmployee {
    let bestMatchedPercentage = 0;
    let bestMatchedEmployee: TwEmployee;

    twEmployees?.forEach((employee) => {
      const percentage = StringUtils.calculateIdenticalPercentageOfTwoString(
        employee.name,
        name
      );

      if (percentage > bestMatchedPercentage) {
        bestMatchedPercentage = percentage;
        bestMatchedEmployee = employee;
      }
    });

    return bestMatchedEmployee;
  }

  public static getTwSkuLinkedWithProduct(
    product: Product,
    twOfferGroups: TwOfferGroup[]
  ): TwSku {
    if (!product.twOfferId || !product.twSkuId) {
      return null;
    }

    let matchedTwOffer: TwOffer;

    twOfferGroups?.forEach((group) => {
      group.offers?.forEach((offer) => {
        if (offer.id === product.twOfferId) {
          matchedTwOffer = offer;
        }
      });
    });

    if (!matchedTwOffer) {
      return null;
    }

    return matchedTwOffer?.skus?.find((sku) => sku.id === product.twSkuId);
  }

  public static generateProductToUpdateComparingWithTwSku(
    product: Product,
    twOffer: TwOffer,
    twSku: TwSku
  ): Product {
    const productToUpdate: Product = {
      ...product,
    };

    if (
      this.isNameOfProductChangedComparingWithTwSku(product, twOffer, twSku)
    ) {
      productToUpdate.name = this.getProductNameFromTwSku(twOffer, twSku);
    }

    if (this.isDurationOfProductChangedComparingWithTwSku(product, twSku)) {
      productToUpdate.duration = twSku.duration;
    }

    if (this.isPriceOfProductChangedComparingWithTwSku(product, twSku)) {
      productToUpdate.price = twSku.fullPriceAmount;
    }

    if (
      this.isBookingVisibilityOfProductChangedComparingWithTwSku(product, twSku)
    ) {
      productToUpdate.enabled4Booking = !!twSku.visible;
    }

    return productToUpdate;
  }

  public static isProductChangedComparingWithTwSku(
    product: Product,
    twOffer: TwOffer,
    twSku: TwSku
  ): boolean {
    return (
      this.isNameOfProductChangedComparingWithTwSku(product, twOffer, twSku) ||
      this.isDurationOfProductChangedComparingWithTwSku(product, twSku) ||
      this.isPriceOfProductChangedComparingWithTwSku(product, twSku) ||
      this.isBookingVisibilityOfProductChangedComparingWithTwSku(product, twSku)
    );
  }

  public static isNameOfProductChangedComparingWithTwSku(
    product: Product,
    twOffer: TwOffer,
    twSku: TwSku
  ): boolean {
    return product.name !== this.getProductNameFromTwSku(twOffer, twSku);
  }

  public static isDurationOfProductChangedComparingWithTwSku(
    product: Product,
    twSku: TwSku
  ): boolean {
    return product.duration !== twSku.duration;
  }

  public static isPriceOfProductChangedComparingWithTwSku(
    product: Product,
    twSku: TwSku
  ): boolean {
    return product.price !== twSku.fullPriceAmount;
  }

  public static isBookingVisibilityOfProductChangedComparingWithTwSku(
    product: Product,
    twSku: TwSku
  ): boolean {
    return !!product.enabled4Booking !== !!twSku.visible;
  }

  public static findMatchedTwSkuForProduct(
    product: Product,
    twGroups: TwOfferGroup[]
  ): [TwOffer, TwSku] {
    if (!product.twOfferId || !product.twSkuId) {
      return null;
    }

    let matchedTwOffer: TwOffer;
    let matchedTwSku: TwSku;

    twGroups.forEach((group) => {
      group.offers?.forEach((offer) => {
        if (offer.id === product.twOfferId) {
          offer.skus?.forEach((sku) => {
            if (sku.id === product.twSkuId) {
              matchedTwOffer = offer;
              matchedTwSku = sku;
            }
          });
        }
      });
    });

    return [matchedTwOffer, matchedTwSku];
  }

  public static getProductNameFromTwSku(
    twOffer: TwOffer,
    twSku: TwSku
  ): string {
    if (twOffer?.skus?.length === 1) {
      return twOffer?.name;
    }

    return `${twOffer?.name} - ${twSku?.name}`;
  }

  public static formatTwMenuForFrontend(twMenu: TwMenu): TwOfferGroup[] {
    // add a noname group, so that all offers without groupId can be added in there
    // all groups are initiated with empty offer array
    const groups: TwOfferGroup[] = [...(twMenu?.groups || []), {}]?.map(
      (g) => ({ ...g, offers: [] })
    );

    twMenu?.offers?.forEach((offer) => {
      if (offer.id && offer.name && offer.skus?.length) {
        // mark as combo
        offer.skus?.forEach((sku) => {
          if (!sku.name && sku.subSkus?.length) {
            sku.name = this.getTwComboSkuName(sku);
            sku.isComboSku = true;
            offer.isComboOffer = true;
          }
        });

        if (offer.skus?.length === 1) {
          // offer with 1 sku has false name (eg. sku.name = "50 minutes")
          offer.skus[0].name = offer.name;
        }

        // add offer to correct group
        const matchedGroupIndex = groups?.findIndex(
          (g) => g.id === offer.groupId
        );
        if (matchedGroupIndex >= 0) {
          groups[matchedGroupIndex].offers.push(offer);
        }
      }
    });

    return groups?.filter((g) => g.active);
  }

  private static getTwComboSkuName(twSku: TwSku): string {
    return twSku?.subSkus?.map((subSku) => subSku?.name).join(' ');
  }

  // endregion
}
